import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Badge,
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  Group,
  Popover,
  Radio,
  ScrollArea,
  Stack,
  Tooltip
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import Select, { StylesConfig } from 'react-select';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  useGetPractitionersByClinicIdQuery,
  useUpdatePagePreferencesMutation,
  useUpdateUserSettingsMutation
} from '@/store/slices/api/userApiSlice';
import { setFilters as setGlobalFilters } from '@/store/slices/preferencesSlice';
import { delay } from '@/utils';
import { NULL_OPTION } from '@/utils/constants';
import {
  accountIdByClinicId,
  isInternalPortalRole,
  practitionerClinicIdsFor,
  SELECT_CLINICS_FOR_PROVIDERS
} from '@/utils/globalFilterClinics';
import useSaveUpdatedReportFields from '@/hooks/useSaveUpdatedReportFields';
import { useUserSettingsFilters } from '@/hooks/useUserSettingsFilters';
import InputLabel from '@/components/InputLabel';
import MultiSelectDropdownWithSearch from '@/components/MultiSelectDropdownWithSearch';
import FilterMenuIcon from '@/assets/icons/FilterMenuIcon';
import { plum } from '@/styles/colors';

import { LabelAndValue } from '@/types/custom';
import { DeviceType, DeviceVendor, getVendorDisplayName } from '@/types/matchDevice';
import LabelText from '@/ui/LabelText';

interface CombinedFiltersDropdownProps {
  iconStyle?: React.CSSProperties;
  customFilterIcon?: React.ReactNode;
  selectedCurrentReportFilters?: string[];
  selectedPreviousReportFilters?: string[];
  isNonPractitionerSignedCRSelected?: boolean;
  isPendingReviewSelectedCRSelected?: boolean;
  isRecentSignedSelected?: boolean;
  isDownloadedSelected?: boolean;
  currentReportType?: 'current' | 'previous';
  recentSignedSortBy?: 'SIGNED_DATE' | 'DATE_OF_SERVICE';
  onApplyReportFilters?: (
    filters: string[],
    isNonPractitioner: boolean,
    isPendingReview: boolean,
    isRecentSigned: boolean,
    isNotDownloaded: boolean,
    reportType: 'current' | 'previous',
    recentSignedSortBy?: 'SIGNED_DATE' | 'DATE_OF_SERVICE'
  ) => void;
  showNoReportOption?: boolean;
  showPreviousReportOptions?: boolean;
}

interface FilterState {
  clinics: LabelAndValue[];
  practitioners: LabelAndValue[];
  signingPractitioners: LabelAndValue[];
  deviceTypes: LabelAndValue[];
  vendors: LabelAndValue[];
  patientStatuses: LabelAndValue[];
}

const FilterOption = memo(
  ({
    options,
    defaultValues,
    onChange,
    label,
    isLoading,
    infoMessage
  }: {
    options: LabelAndValue[];
    defaultValues: LabelAndValue[];
    onChange: (values: LabelAndValue[]) => void;
    label: string;
    isLoading?: boolean;
    infoMessage?: string | null;
  }) => (
    <MultiSelectDropdownWithSearch
      options={options}
      defaultValues={defaultValues}
      onChange={onChange}
      label={label}
      maxItemsToDisplay={1}
      isLoading={isLoading}
      infoMessage={infoMessage}
    />
  )
);

FilterOption.displayName = 'FilterOption';

const DEVICE_TYPE_OPTIONS: LabelAndValue[] = [
  NULL_OPTION,
  ...Object.values(DeviceType).map((deviceType) => ({
    label: deviceType,
    value: deviceType
  }))
];

const VENDOR_OPTIONS: LabelAndValue[] = Object.values(DeviceVendor).map((vendor) => ({
  label: getVendorDisplayName(vendor),
  value: vendor
}));

const PATIENT_STATUS_OPTIONS: LabelAndValue[] = [
  { label: 'Active', value: 'Active' },
  { label: 'Pending', value: 'Candidate' },
  { label: 'Inactive', value: 'Inactive' },
  { label: 'Paused', value: 'Paused' }
];

const FILTER_OPTIONS = [
  { value: 'GREEN', label: 'Routine' },
  { value: 'YELLOW', label: 'Yellow Alert' },
  { value: 'RED', label: 'Red Alert' }
];

const REPORT_TYPE_OPTIONS: LabelAndValue[] = [
  { value: 'none', label: 'No selection' },
  { value: 'current', label: 'Current Reports' },
  { value: 'previous', label: 'Previous Reports' }
];

const reportTypeSelectStyles: StylesConfig<LabelAndValue, false> = {
  control: (styles, { isFocused }) => ({
    ...styles,
    backgroundColor: 'white',
    borderColor: isFocused ? plum[6] : plum[3],
    '&:hover': {
      borderColor: plum[5]
    },
    cursor: 'pointer',
    minHeight: '35px'
  }),
  option: (styles, { isSelected, isFocused }) => ({
    ...styles,
    backgroundColor: isSelected ? plum[5] : isFocused ? plum[2] : 'transparent',
    color: isSelected ? 'white' : plum[8],
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: isSelected ? plum[6] : plum[2]
    }
  }),
  singleValue: (styles) => ({
    ...styles,
    color: plum[9]
  }),
  dropdownIndicator: (styles) => ({
    ...styles,
    color: plum[6],
    ':hover': {
      color: plum[7]
    }
  }),
  indicatorSeparator: (styles) => ({
    ...styles,
    backgroundColor: plum[4]
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999
  })
};

const CombinedFiltersDropdown = ({
  selectedCurrentReportFilters = [],
  selectedPreviousReportFilters = [],
  isNonPractitionerSignedCRSelected = false,
  isPendingReviewSelectedCRSelected = false,
  isRecentSignedSelected = false,
  isDownloadedSelected = false,
  currentReportType = 'current',
  recentSignedSortBy: initialRecentSignedSortBy = 'SIGNED_DATE',
  onApplyReportFilters,
  showNoReportOption = true,
  showPreviousReportOptions = true
}: CombinedFiltersDropdownProps) => {
  const dispatch = useAppDispatch();
  const [opened, { open, close }] = useDisclosure();
  const { filters: globalFilters, userSettingsId, userSettingsData } = useUserSettingsFilters();
  const { reportUpdateFields } = useAppSelector((state) => state.transmissionReports);
  const { user } = useAppSelector((state) => state.auth);
  const saveUpdatedReportFields = useSaveUpdatedReportFields();

  const [updateUserSettings, { isLoading: isUpdatingFilter }] = useUpdateUserSettingsMutation();
  const [updatePagePreferences] = useUpdatePagePreferencesMutation();

  const isDropdownOpenRef = useRef(false);

  const {
    defaultClinics,
    defaultPractitioners,
    defaultSigningPractitioners,
    defaultDeviceTypes,
    defaultVendors
  } = useMemo(
    () => ({
      defaultClinics: globalFilters?.clinics ?? [],
      defaultPractitioners: globalFilters?.practitioners ?? [],
      defaultSigningPractitioners: globalFilters?.signingPractitioners ?? [],
      defaultDeviceTypes: globalFilters?.deviceTypes ?? [],
      defaultVendors: globalFilters?.vendors ?? []
    }),
    [globalFilters]
  );

  // patientStatuses: API data takes priority, fall back to globalFilters
  const defaultPatientStatuses = useMemo(
    () => globalFilters?.patientStatuses ?? [],
    [globalFilters?.patientStatuses]
  );

  const [filters, setFilters] = useState<FilterState>({
    clinics: defaultClinics,
    practitioners: defaultPractitioners,
    signingPractitioners: defaultSigningPractitioners,
    deviceTypes: defaultDeviceTypes,
    vendors: defaultVendors,
    patientStatuses: defaultPatientStatuses
  });

  // Initialize patientStatuses from user preferences (API data) when available,
  // fall back to globalFilters — mirrors FiltersButton behaviour
  useEffect(() => {
    if (userSettingsData?.patientListPagePreferences?.patientStatuses) {
      const statusValues = userSettingsData.patientListPagePreferences.patientStatuses;
      if (statusValues.length > 0) {
        setFilters((prev) => ({
          ...prev,
          patientStatuses: PATIENT_STATUS_OPTIONS.filter((opt) => statusValues.includes(opt.value))
        }));
      } else {
        setFilters((prev) => ({ ...prev, patientStatuses: [] }));
      }
    } else {
      setFilters((prev) => ({
        ...prev,
        patientStatuses: globalFilters?.patientStatuses ?? []
      }));
    }
  }, [userSettingsData, globalFilters?.patientStatuses]);

  // Initialize with user preferences or provided defaults
  const [reportType, setReportType] = useState<'current' | 'previous' | null>(currentReportType);
  const [tempCurrentReportFilters, setTempCurrentReportFilters] = useState<string[]>(
    selectedCurrentReportFilters
  );
  const [tempPreviousReportFilters, setTempPreviousReportFilters] = useState<string[]>(
    selectedPreviousReportFilters
  );
  const [tempNonPractitionerSelected, setTempNonPractitionerSelected] = useState(
    isNonPractitionerSignedCRSelected
  );
  const [tempPendingReviewSelected, setTempPendingReviewSelected] = useState(
    isPendingReviewSelectedCRSelected
  );
  const [tempRecentSignedSelected, setTempRecentSignedSelected] = useState(isRecentSignedSelected);
  const [tempDownloadedSelected, setTempDownloadedSelected] = useState(isDownloadedSelected);
  const [recentSignedSortBy, setRecentSignedSortBy] = useState<'SIGNED_DATE' | 'DATE_OF_SERVICE'>(
    initialRecentSignedSortBy
  );

  // Initialize with user preferences when available
  useEffect(() => {
    if (userSettingsData?.patientListPagePreferences) {
      const prefs = userSettingsData.patientListPagePreferences;

      // Determine report type based on existing filter data if no explicit reportType exists
      const hasCurrentPrefs =
        (prefs.currentReportFilters?.colors && prefs.currentReportFilters.colors.length > 0) ||
        prefs.currentReportFilters?.isNonPractitionerSigned === true ||
        prefs.currentReportFilters?.isPendingReview === true;

      const hasPreviousPrefs =
        (prefs.previousReportFilters?.colors && prefs.previousReportFilters.colors.length > 0) ||
        prefs.previousReportFilters?.isRecentSigned === true ||
        prefs.previousReportFilters?.isDownloaded === true;

      if (hasCurrentPrefs) {
        setReportType('current');
      } else if (hasPreviousPrefs) {
        setReportType('previous');
      } else {
        setReportType(null);
      }

      // Load current report filters
      if (prefs.currentReportFilters) {
        const currentFilters = prefs.currentReportFilters;
        if (currentFilters.colors) {
          setTempCurrentReportFilters(currentFilters.colors);
        }
        if (currentFilters.isNonPractitionerSigned !== undefined) {
          setTempNonPractitionerSelected(currentFilters.isNonPractitionerSigned);
        }
        if (currentFilters.isPendingReview !== undefined) {
          setTempPendingReviewSelected(currentFilters.isPendingReview);
        }
      }

      // Load previous report filters
      if (prefs.previousReportFilters) {
        const previousFilters = prefs.previousReportFilters;
        if (previousFilters.colors) {
          setTempPreviousReportFilters(previousFilters.colors);
        }
        if (previousFilters.isRecentSigned !== undefined) {
          setTempRecentSignedSelected(previousFilters.isRecentSigned);
        }
        if (previousFilters.recentSignedSortBy !== undefined) {
          setRecentSignedSortBy(previousFilters.recentSignedSortBy);
        }
        if (previousFilters.isDownloaded !== undefined) {
          setTempDownloadedSelected(previousFilters.isDownloaded);
        }
      }
    }
  }, [userSettingsData]);
  const [showRecentSignedSubmenu, setShowRecentSignedSubmenu] = useState(false);

  const selectedClinicIds = useMemo(
    () => filters.clinics.map((clinic) => clinic.value),
    [filters.clinics]
  );
  const accessibleClinicIds = useMemo(
    () => userSettingsData?.clinics?.map((clinic) => clinic.id) ?? [],
    [userSettingsData]
  );
  const clinicAccountIds = useMemo(
    () => accountIdByClinicId(userSettingsData?.clinics ?? []),
    [userSettingsData?.clinics]
  );
  const practitionerClinicIds = useMemo(
    () =>
      practitionerClinicIdsFor(
        selectedClinicIds,
        accessibleClinicIds,
        isInternalPortalRole(user?.role),
        clinicAccountIds
      ),
    [selectedClinicIds, accessibleClinicIds, user?.role, clinicAccountIds]
  );
  const {
    data: practitionerData,
    isLoading: practitionerOptionsLoading,
    isFetching: practitionerOptionsFetching
  } = useGetPractitionersByClinicIdQuery(
    { clinicIds: practitionerClinicIds },
    { skip: practitionerClinicIds.length === 0 }
  );

  useEffect(() => {
    if (!isDropdownOpenRef.current) {
      setFilters((prev) => ({
        ...prev,
        clinics: defaultClinics,
        practitioners: defaultPractitioners,
        signingPractitioners: defaultSigningPractitioners,
        deviceTypes: defaultDeviceTypes,
        vendors: defaultVendors
        // patientStatuses is managed separately by its own effects
      }));
    }
  }, [
    defaultClinics,
    defaultPractitioners,
    defaultSigningPractitioners,
    defaultDeviceTypes,
    defaultVendors
  ]);

  useEffect(() => {
    if (!isDropdownOpenRef.current) {
      // If user settings are loaded, those take priority — don't override with prop defaults
      if (userSettingsData?.patientListPagePreferences) return;

      let actualReportType: 'current' | 'previous' | null = null;

      const hasCurrentFilters =
        selectedCurrentReportFilters.length > 0 ||
        isNonPractitionerSignedCRSelected ||
        isPendingReviewSelectedCRSelected;

      const hasPreviousFilters =
        selectedPreviousReportFilters.length > 0 || isRecentSignedSelected || isDownloadedSelected;

      if (hasCurrentFilters) {
        actualReportType = 'current';
      } else if (hasPreviousFilters) {
        actualReportType = 'previous';
      }
      // If neither has filters, actualReportType stays null

      setReportType(actualReportType);
      setTempCurrentReportFilters(selectedCurrentReportFilters);
      setTempPreviousReportFilters(selectedPreviousReportFilters);
      setTempNonPractitionerSelected(isNonPractitionerSignedCRSelected);
      setTempPendingReviewSelected(isPendingReviewSelectedCRSelected);
      setTempRecentSignedSelected(isRecentSignedSelected);
      setTempDownloadedSelected(isDownloadedSelected);
      setRecentSignedSortBy(initialRecentSignedSortBy);
    }
  }, [
    userSettingsData,
    selectedCurrentReportFilters,
    selectedPreviousReportFilters,
    isNonPractitionerSignedCRSelected,
    isPendingReviewSelectedCRSelected,
    isRecentSignedSelected,
    isDownloadedSelected,
    initialRecentSignedSortBy
  ]);

  const clinicOptions = useMemo(
    () =>
      userSettingsData?.clinics
        ?.map((clinic) => ({
          label: clinic.name,
          value: clinic.id,
          desc: clinic?.parent?.name ?? ''
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) ?? [],
    [userSettingsData]
  );

  // Check if clinic options have multiple accounts
  const hasMultipleAccountsInOptions = useMemo(() => {
    const accounts = new Set(clinicOptions.map((clinic) => clinic.desc || 'No Account'));
    return accounts.size > 1;
  }, [clinicOptions]);

  // Check if selected clinics belong to different accounts
  const hasMultipleAccountsSelected = useMemo(() => {
    const accounts = new Set(
      filters.clinics.map((clinic) => {
        const option = clinicOptions.find((opt) => opt.value === clinic.value);
        return option?.desc || 'No Account';
      })
    );
    return accounts.size > 1;
  }, [filters.clinics, clinicOptions]);

  const shouldShowTooltip = hasMultipleAccountsInOptions && hasMultipleAccountsSelected;

  const practitionerOptions = useMemo(() => {
    if (!practitionerData?.data) return [NULL_OPTION];

    const options = practitionerData.data
      .map((practitioner) => ({
        label: practitioner.name,
        value: practitioner.id
      }))
      .reduce<LabelAndValue[]>(
        (acc, current) =>
          acc.some((item) => item.value === current.value) ? acc : [...acc, current],
        []
      )
      .sort((a, b) => a.label.localeCompare(b.label));

    return [NULL_OPTION, ...options];
  }, [practitionerData]);

  const updateFilter = useCallback(
    (key: keyof FilterState) => (values: LabelAndValue[]) => {
      if (key === 'clinics' && hasMultipleAccountsInOptions) {
        const selectedAccounts = new Set(
          values.map((clinic) => {
            const option = clinicOptions.find((opt) => opt.value === clinic.value);
            return option?.desc || 'No Account';
          })
        );
        const previousValues = filters.clinics;
        const added = values.filter(
          (value) => !previousValues.some((prev) => prev.value === value.value)
        );

        if (selectedAccounts.size > 1 && added.length === 1) {
          const newAccount =
            clinicOptions.find((opt) => opt.value === added[0].value)?.desc || 'No Account';
          const filteredClinics = values.filter((clinic) => {
            const option = clinicOptions.find((opt) => opt.value === clinic.value);
            return (option?.desc || 'No Account') === newAccount;
          });

          setFilters((prev) => ({
            ...prev,
            practitioners: [],
            signingPractitioners: [],
            clinics: filteredClinics
          }));
          return;
        }
      }

      setFilters((prev) => ({
        ...prev,
        practitioners: key === 'clinics' ? [] : prev.practitioners,
        signingPractitioners: key === 'clinics' ? [] : prev.signingPractitioners,
        [key]: values
      }));
    },
    [clinicOptions, filters.clinics, hasMultipleAccountsInOptions]
  );

  const handleReportFilterToggle = useCallback(
    (filterType: string) => {
      if (reportType === 'current') {
        setTempCurrentReportFilters((prev) =>
          prev.includes(filterType) ? prev.filter((f) => f !== filterType) : [...prev, filterType]
        );
      } else if (reportType === 'previous') {
        setTempPreviousReportFilters((prev) =>
          prev.includes(filterType) ? prev.filter((f) => f !== filterType) : [...prev, filterType]
        );
      }
    },
    [reportType]
  );

  const isAllReportFiltersSelected = useMemo(() => {
    if (!reportType) return false;

    const allBaseFiltersSelected = FILTER_OPTIONS.every((opt) =>
      reportType === 'current'
        ? tempCurrentReportFilters.includes(opt.value)
        : tempPreviousReportFilters.includes(opt.value)
    );

    if (reportType === 'current') {
      return (
        allBaseFiltersSelected &&
        (showNoReportOption ? tempNonPractitionerSelected && tempPendingReviewSelected : true)
      );
    } else {
      return (
        allBaseFiltersSelected &&
        (showPreviousReportOptions ? tempRecentSignedSelected && tempDownloadedSelected : true)
      );
    }
  }, [
    reportType,
    tempCurrentReportFilters,
    tempPreviousReportFilters,
    tempNonPractitionerSelected,
    tempPendingReviewSelected,
    tempRecentSignedSelected,
    tempDownloadedSelected,
    showNoReportOption,
    showPreviousReportOptions
  ]);

  const handleSelectAllToggle = useCallback(() => {
    if (!reportType) return;

    if (isAllReportFiltersSelected) {
      if (reportType === 'current') {
        setTempCurrentReportFilters([]);
        if (showNoReportOption) setTempNonPractitionerSelected(false);
        if (showNoReportOption) setTempPendingReviewSelected(false);
      } else {
        setTempPreviousReportFilters([]);
        if (showPreviousReportOptions) {
          setTempRecentSignedSelected(false);
          setTempDownloadedSelected(false);
        }
      }
    } else {
      if (reportType === 'current') {
        setTempCurrentReportFilters(FILTER_OPTIONS.map((opt) => opt.value));
        if (showNoReportOption) setTempNonPractitionerSelected(true);
        if (showNoReportOption) setTempPendingReviewSelected(true);
      } else {
        setTempPreviousReportFilters(FILTER_OPTIONS.map((opt) => opt.value));
        if (showPreviousReportOptions) {
          setTempRecentSignedSelected(true);
          setTempDownloadedSelected(true);
        }
      }
    }
  }, [reportType, isAllReportFiltersSelected, showNoReportOption, showPreviousReportOptions]);

  const handleApply = useCallback(async () => {
    try {
      const includeUnassignedPractitioner = filters.practitioners.some((p) => p.value === 'null');
      const includeUnassignedSigningPractitioner = filters.signingPractitioners.some(
        (s) => s.value === 'null'
      );

      const filterPayload = {
        organizationIds: filters.clinics.map((c) => c.value),
        practitionerIds: filters.practitioners.map((p) => p.value),
        deviceTypes: filters.deviceTypes.map((d) => d.value),
        vendors: filters.vendors.map((v) => v.value),
        signingPractitionerIds: filters.signingPractitioners.map((s) => s.value),
        patientStatuses: filters.patientStatuses.map((s) => s.value)
      };

      if (userSettingsId) {
        // Update global filter settings to the existing endpoint
        await updateUserSettings({
          settingsId: userSettingsId,
          organizationIds: filterPayload.organizationIds,
          practitionerIds: filterPayload.practitionerIds.filter((id) => id !== 'null'),
          signingPractitionerIds: filterPayload.signingPractitionerIds.filter(
            (id) => id !== 'null'
          ),
          deviceTypes: filterPayload.deviceTypes,
          vendors: filterPayload.vendors,
          includeUnassignedPractitioner,
          includeUnassignedSigningPractitioner
        }).unwrap();

        // Update specific page preferences (report filters and related settings) to new endpoint
        await updatePagePreferences({
          settingsId: userSettingsId,
          patientListPagePreferences: {
            patientStatuses: filters.patientStatuses.map((s) => s.value),
            reportType: reportType,
            currentReportFilters: {
              colors: reportType === 'current' ? tempCurrentReportFilters : [],
              isNonPractitionerSigned:
                reportType === 'current' ? tempNonPractitionerSelected : false,
              isPendingReview: reportType === 'current' ? tempPendingReviewSelected : false
            },
            previousReportFilters: {
              colors: reportType === 'previous' ? tempPreviousReportFilters : [],
              isRecentSigned: reportType === 'previous' ? tempRecentSignedSelected : false,
              recentSignedSortBy: reportType === 'previous' ? recentSignedSortBy : 'SIGNED_DATE',
              isDownloaded: reportType === 'previous' ? tempDownloadedSelected : false
            },
            density: undefined,
            columns: undefined
          }
        }).unwrap();
      }

      dispatch(
        setGlobalFilters({
          clinics: filters.clinics,
          practitioners: filters.practitioners,
          signingPractitioners: filters.signingPractitioners,
          deviceTypes: filters.deviceTypes,
          vendors: filters.vendors,
          patientStatuses: filters.patientStatuses
        })
      );

      if (onApplyReportFilters) {
        if (reportType === 'current') {
          onApplyReportFilters(
            tempCurrentReportFilters,
            tempNonPractitionerSelected,
            tempPendingReviewSelected,
            false,
            false,
            'current'
          );
        } else if (reportType === 'previous') {
          onApplyReportFilters(
            tempPreviousReportFilters,
            false,
            false,
            tempRecentSignedSelected,
            tempDownloadedSelected,
            'previous',
            tempRecentSignedSelected ? recentSignedSortBy : undefined
          );
        } else if (reportType === null) {
          onApplyReportFilters([], false, false, false, false, 'current');
        }
      }

      isDropdownOpenRef.current = false;
      close();
    } catch (error) {
      console.error('Failed to update filters:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to update filters',
        color: 'red'
      });
    }
  }, [
    updateUserSettings,
    updatePagePreferences,
    userSettingsId,
    dispatch,
    filters,
    onApplyReportFilters,
    reportType,
    tempCurrentReportFilters,
    tempPreviousReportFilters,
    tempNonPractitionerSelected,
    tempPendingReviewSelected,
    tempRecentSignedSelected,
    tempDownloadedSelected,
    recentSignedSortBy,
    close
  ]);

  const isApplyDisabled = useMemo(() => {
    const globalFiltersChanged =
      JSON.stringify(filters.clinics) !== JSON.stringify(defaultClinics) ||
      JSON.stringify(filters.practitioners) !== JSON.stringify(defaultPractitioners) ||
      JSON.stringify(filters.signingPractitioners) !==
        JSON.stringify(defaultSigningPractitioners) ||
      JSON.stringify(filters.deviceTypes) !== JSON.stringify(defaultDeviceTypes) ||
      JSON.stringify(filters.vendors) !== JSON.stringify(defaultVendors) ||
      JSON.stringify(filters.patientStatuses) !== JSON.stringify(defaultPatientStatuses);

    const reportFiltersChanged =
      reportType !== currentReportType ||
      JSON.stringify(tempCurrentReportFilters) !== JSON.stringify(selectedCurrentReportFilters) ||
      JSON.stringify(tempPreviousReportFilters) !== JSON.stringify(selectedPreviousReportFilters) ||
      tempNonPractitionerSelected !== isNonPractitionerSignedCRSelected ||
      tempPendingReviewSelected !== isPendingReviewSelectedCRSelected ||
      tempRecentSignedSelected !== isRecentSignedSelected ||
      tempDownloadedSelected !== isDownloadedSelected ||
      recentSignedSortBy !== initialRecentSignedSortBy;

    return !globalFiltersChanged && !reportFiltersChanged;
  }, [
    filters,
    defaultClinics,
    defaultPractitioners,
    defaultSigningPractitioners,
    defaultDeviceTypes,
    defaultVendors,
    defaultPatientStatuses,
    reportType,
    currentReportType,
    tempCurrentReportFilters,
    selectedCurrentReportFilters,
    tempPreviousReportFilters,
    selectedPreviousReportFilters,
    tempNonPractitionerSelected,
    isNonPractitionerSignedCRSelected,
    isPendingReviewSelectedCRSelected,
    tempPendingReviewSelected,
    tempRecentSignedSelected,
    isRecentSignedSelected,
    tempDownloadedSelected,
    isDownloadedSelected,
    recentSignedSortBy,
    initialRecentSignedSortBy
  ]);

  const isClearAllDisabled = useMemo(() => {
    const isGlobalFiltersEmpty =
      filters.clinics.length === 0 &&
      filters.practitioners.length === 0 &&
      filters.signingPractitioners.length === 0 &&
      filters.deviceTypes.length === 0 &&
      filters.vendors.length === 0 &&
      filters.patientStatuses.length === 0;

    const isReportFiltersEmpty =
      reportType === null &&
      tempCurrentReportFilters.length === 0 &&
      tempPreviousReportFilters.length === 0 &&
      !tempNonPractitionerSelected &&
      !tempPendingReviewSelected &&
      !tempRecentSignedSelected &&
      !tempDownloadedSelected;

    return isGlobalFiltersEmpty && isReportFiltersEmpty;
  }, [
    filters,
    reportType,
    tempCurrentReportFilters.length,
    tempPreviousReportFilters.length,
    tempNonPractitionerSelected,
    tempRecentSignedSelected,
    tempPendingReviewSelected,
    tempDownloadedSelected
  ]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;

    // Mirror the same fallback logic as PatientListPage:
    // use globalFilters if populated (non-null & non-empty), otherwise fall back to userSettingsData
    const activeClinics =
      globalFilters !== null && (globalFilters?.clinics?.length ?? 0) > 0
        ? globalFilters.clinics
        : (userSettingsData?.filteredClinics ?? []);

    const activePractitioners =
      globalFilters !== null && (globalFilters?.practitioners?.length ?? 0) > 0
        ? globalFilters.practitioners
        : (userSettingsData?.filteredPractitioners ?? []);

    const activeSigningPractitioners =
      globalFilters !== null && (globalFilters?.signingPractitioners?.length ?? 0) > 0
        ? globalFilters.signingPractitioners
        : (userSettingsData?.filteredSigningPractitioners ?? []);

    const activeDeviceTypes =
      globalFilters !== null && (globalFilters?.deviceTypes?.length ?? 0) > 0
        ? globalFilters.deviceTypes
        : (userSettingsData?.deviceTypes?.map((d) => ({ label: d, value: d })) ?? []);

    const activeVendors =
      globalFilters !== null && (globalFilters?.vendors?.length ?? 0) > 0
        ? globalFilters.vendors
        : (userSettingsData?.vendors?.map((v) => ({ label: v, value: v })) ?? []);

    const savedStatuses =
      userSettingsData?.patientStatuses && userSettingsData.patientStatuses.length > 0
        ? userSettingsData.patientStatuses
        : (userSettingsData?.patientListPagePreferences?.patientStatuses ?? []);
    const activePatientStatuses =
      globalFilters !== null && (globalFilters?.patientStatuses?.length ?? 0) > 0
        ? globalFilters.patientStatuses
        : PATIENT_STATUS_OPTIONS.filter((o) => savedStatuses.includes(o.value));

    if (activeClinics.length > 0) count += 1;
    if (activePractitioners.length > 0) count += 1;
    if (activeSigningPractitioners.length > 0) count += 1;
    if (activeDeviceTypes.length > 0) count += 1;
    if (activeVendors.length > 0) count += 1;
    if (activePatientStatuses.length > 0) count += 1;

    // Report type filters: use props if populated (Redux already seeded),
    // otherwise fall back to userSettingsData preferences directly
    const prefs = userSettingsData?.patientListPagePreferences;

    const activeCurrentColors =
      selectedCurrentReportFilters.length > 0
        ? selectedCurrentReportFilters
        : (prefs?.currentReportFilters?.colors ?? []);
    const activeIsNonPractitioner =
      isNonPractitionerSignedCRSelected ||
      prefs?.currentReportFilters?.isNonPractitionerSigned === true;
    const activeIsPendingReview =
      isPendingReviewSelectedCRSelected || prefs?.currentReportFilters?.isPendingReview === true;

    const activePreviousColors =
      selectedPreviousReportFilters.length > 0
        ? selectedPreviousReportFilters
        : (prefs?.previousReportFilters?.colors ?? []);
    const activeIsRecentSigned =
      isRecentSignedSelected || prefs?.previousReportFilters?.isRecentSigned === true;
    const activeIsDownloaded =
      isDownloadedSelected || prefs?.previousReportFilters?.isDownloaded === true;

    const hasCurrentFilters =
      activeCurrentColors.length > 0 || activeIsNonPractitioner || activeIsPendingReview;
    const hasPreviousFilters =
      activePreviousColors.length > 0 || activeIsRecentSigned || activeIsDownloaded;

    if (hasCurrentFilters || hasPreviousFilters) {
      count += 1;
    }

    return count;
  }, [
    globalFilters,
    userSettingsData,
    selectedCurrentReportFilters,
    selectedPreviousReportFilters,
    isNonPractitionerSignedCRSelected,
    isPendingReviewSelectedCRSelected,
    isRecentSignedSelected,
    isDownloadedSelected
  ]);

  const onOpenClick = async () => {
    if (reportUpdateFields && Object.keys(reportUpdateFields).length > 0) {
      saveUpdatedReportFields();
      await delay(1000);
    }
    isDropdownOpenRef.current = true;
    open();
  };

  const onCloseClick = useCallback(() => {
    isDropdownOpenRef.current = false;
    close();

    // Determine the actual report type based on what filters are currently applied
    let actualReportType: 'current' | 'previous' | null = null;

    const hasCurrentFilters =
      selectedCurrentReportFilters.length > 0 ||
      isNonPractitionerSignedCRSelected ||
      isPendingReviewSelectedCRSelected;

    const hasPreviousFilters =
      selectedPreviousReportFilters.length > 0 || isRecentSignedSelected || isDownloadedSelected;

    if (hasCurrentFilters) {
      actualReportType = 'current';
    } else if (hasPreviousFilters) {
      actualReportType = 'previous';
    }
    // If neither has filters, actualReportType stays null

    setFilters((prev) => ({
      ...prev,
      clinics: defaultClinics,
      practitioners: defaultPractitioners,
      signingPractitioners: defaultSigningPractitioners,
      deviceTypes: defaultDeviceTypes,
      vendors: defaultVendors,
      patientStatuses: globalFilters?.patientStatuses ?? []
    }));
    setReportType(actualReportType);
    setTempCurrentReportFilters(selectedCurrentReportFilters);
    setTempPreviousReportFilters(selectedPreviousReportFilters);
    setTempNonPractitionerSelected(isNonPractitionerSignedCRSelected);
    setTempPendingReviewSelected(isPendingReviewSelectedCRSelected);
    setTempRecentSignedSelected(isRecentSignedSelected);
    setTempDownloadedSelected(isDownloadedSelected);
    setRecentSignedSortBy(initialRecentSignedSortBy);
    setShowRecentSignedSubmenu(false);
  }, [
    close,
    defaultClinics,
    defaultPractitioners,
    defaultSigningPractitioners,
    defaultDeviceTypes,
    defaultVendors,
    defaultPatientStatuses,
    selectedCurrentReportFilters,
    selectedPreviousReportFilters,
    isNonPractitionerSignedCRSelected,
    isPendingReviewSelectedCRSelected,
    isRecentSignedSelected,
    isDownloadedSelected,
    initialRecentSignedSortBy
  ]);

  const clearAllFilters = useCallback(() => {
    setFilters({
      clinics: [],
      practitioners: [],
      signingPractitioners: [],
      deviceTypes: [],
      vendors: [],
      patientStatuses: []
    });
    setReportType(null);
    setTempCurrentReportFilters([]);
    setTempPreviousReportFilters([]);
    setTempNonPractitionerSelected(false);
    setTempPendingReviewSelected(false);
    setTempRecentSignedSelected(false);
    setTempDownloadedSelected(false);
    setRecentSignedSortBy('SIGNED_DATE');
  }, []);

  const activeReportFilters =
    reportType === 'current' ? tempCurrentReportFilters : tempPreviousReportFilters;

  return (
    <Popover
      position="bottom-end"
      shadow="md"
      opened={opened}
      width={380}
      onClose={onCloseClick}
      withinPortal>
      <Popover.Target>
        <Button variant="transparent" onClick={onOpenClick}>
          FILTERS
          {activeFiltersCount > 0 && (
            <Badge variant="filled" size="xs" circle pos="absolute" top={0} right={5}>
              {activeFiltersCount}
            </Badge>
          )}
          <span style={{ marginLeft: '10px' }}>
            <FilterMenuIcon />
          </span>
        </Button>
      </Popover.Target>

      <Popover.Dropdown p={0} style={{ overflow: 'hidden' }}>
        <Box
          style={{
            backgroundColor: '#C9BBBE',
            borderBottom: '1px solid #E0E0E0'
          }}>
          <Group justify="space-between" p="md" pb="sm">
            <Box fw={600} fz="lg" c="#572B3E">
              Filters
            </Box>
          </Group>
        </Box>

        <ScrollArea h={500} type="auto" offsetScrollbars>
          <Box px="md">
            <Stack gap="xs" py="md">
              <Box>
                <MultiSelectDropdownWithSearch
                  options={clinicOptions}
                  defaultValues={filters.clinics}
                  onChange={updateFilter('clinics')}
                  label="Clinics"
                  maxItemsToDisplay={1}
                  showTooltip={hasMultipleAccountsInOptions}
                  tooltipMessage="Selecting a clinic from a different account will clear your previous clinic selection"
                  infoMessage={
                    hasMultipleAccountsInOptions
                      ? 'Selecting a clinic in another account resets your selection'
                      : null
                  }
                />
              </Box>

              <FilterOption
                key={`following-${filters.clinics.map((clinic) => clinic.value).join(',')}-${practitionerOptions.length}`}
                options={practitionerOptions}
                defaultValues={filters.practitioners}
                onChange={updateFilter('practitioners')}
                label="Following Practitioners"
                isLoading={practitionerOptionsLoading || practitionerOptionsFetching}
                infoMessage={
                  practitionerClinicIds.length === 0 ? SELECT_CLINICS_FOR_PROVIDERS : null
                }
              />
              <FilterOption
                key={`signing-${filters.clinics.map((clinic) => clinic.value).join(',')}-${practitionerOptions.length}`}
                options={practitionerOptions}
                defaultValues={filters.signingPractitioners}
                onChange={updateFilter('signingPractitioners')}
                label="Signing Practitioners"
                isLoading={practitionerOptionsLoading || practitionerOptionsFetching}
                infoMessage={
                  practitionerClinicIds.length === 0 ? SELECT_CLINICS_FOR_PROVIDERS : null
                }
              />
              <FilterOption
                options={DEVICE_TYPE_OPTIONS}
                defaultValues={filters.deviceTypes}
                onChange={updateFilter('deviceTypes')}
                label="Device Types"
              />
              <FilterOption
                options={VENDOR_OPTIONS}
                defaultValues={filters.vendors}
                onChange={updateFilter('vendors')}
                label="Vendors"
              />
              <Box>
                <InputLabel label="Status" name="patient-status" labelColor="#A27E8A" />
                <Group gap="md">
                  {PATIENT_STATUS_OPTIONS.map((option) => (
                    <Checkbox
                      key={option.value}
                      label={option.label}
                      checked={filters.patientStatuses.some((s) => s.value === option.value)}
                      onChange={(e) => {
                        const checked = e.currentTarget.checked;
                        if (checked) {
                          updateFilter('patientStatuses')([...filters.patientStatuses, option]);
                        } else {
                          updateFilter('patientStatuses')(
                            filters.patientStatuses.filter((s) => s.value !== option.value)
                          );
                        }
                      }}
                    />
                  ))}
                </Group>
              </Box>
            </Stack>

            <Stack gap="xs">
              <Box>
                <InputLabel label="Report Type" name="report-type" labelColor="#A27E8A" />
                <Select
                  value={REPORT_TYPE_OPTIONS.find(
                    (option) => option.value === (reportType || 'none')
                  )}
                  onChange={(option) => {
                    const value = option?.value;
                    setReportType(value === 'none' ? null : (value as 'current' | 'previous'));
                  }}
                  options={REPORT_TYPE_OPTIONS}
                  placeholder="Select report type"
                  styles={reportTypeSelectStyles}
                  menuPortalTarget={document.body}
                  theme={(theme) => ({
                    ...theme,
                    colors: {
                      ...theme.colors,
                      primary: plum[6],
                      primary75: plum[5],
                      primary50: plum[4],
                      primary25: plum[2]
                    }
                  })}
                />
              </Box>

              {reportType && (
                <Stack mt="xs">
                  <Group justify="space-between">
                    <LabelText
                      label={reportType === 'current' ? 'Current Reports' : 'Previous Reports'}
                    />
                    <Button size="xs" variant="subtle" onClick={handleSelectAllToggle} color="plum">
                      {isAllReportFiltersSelected ? 'Deselect All' : 'Select All'}
                    </Button>
                  </Group>

                  <Divider mx={-16} mt={-16} />

                  <Stack gap="xs">
                    {FILTER_OPTIONS.map(({ value, label }) => (
                      <Box key={value}>
                        <Checkbox
                          checked={activeReportFilters.includes(value)}
                          onChange={() => handleReportFilterToggle(value)}
                          label={label}
                          mb="xs"
                        />
                        <Divider />
                      </Box>
                    ))}

                    {reportType === 'current' && showNoReportOption && (
                      <>
                        <Box>
                          <Checkbox
                            checked={tempNonPractitionerSelected}
                            onChange={() => setTempNonPractitionerSelected((prev) => !prev)}
                            label="Pending Physician Review"
                            mb="xs"
                          />
                          <Divider />
                        </Box>
                        <Box>
                          <Checkbox
                            checked={tempPendingReviewSelected}
                            onChange={() => setTempPendingReviewSelected((prev) => !prev)}
                            label="Regular Pending Review"
                            mb="xs"
                          />
                          <Divider />
                        </Box>
                      </>
                    )}

                    {reportType === 'previous' && showPreviousReportOptions && (
                      <>
                        <Box>
                          <Group justify="space-between" wrap="nowrap">
                            <Checkbox
                              checked={tempRecentSignedSelected}
                              onChange={() => setTempRecentSignedSelected((prev) => !prev)}
                              label="Recently Signed (Last 30 days)"
                              mb="xs"
                              styles={{
                                label: { flex: 1 }
                              }}
                            />
                            {tempRecentSignedSelected && (
                              <Box
                                onClick={() => setShowRecentSignedSubmenu((prev) => !prev)}
                                style={{
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '4px',
                                  marginTop: '-6px',
                                  transition: 'transform 0.2s'
                                }}>
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  style={{
                                    transform: showRecentSignedSubmenu
                                      ? 'rotate(90deg)'
                                      : 'rotate(0deg)',
                                    transition: 'transform 0.2s'
                                  }}>
                                  <polyline points="9 18 15 12 9 6" />
                                </svg>
                              </Box>
                            )}
                          </Group>
                          <Divider />
                          <Collapse in={tempRecentSignedSelected && showRecentSignedSubmenu}>
                            <Box pl="lg" py="xs">
                              <Radio.Group
                                value={recentSignedSortBy}
                                onChange={(value) =>
                                  setRecentSignedSortBy(value as 'SIGNED_DATE' | 'DATE_OF_SERVICE')
                                }>
                                <Stack gap="xs">
                                  <Radio value="SIGNED_DATE" label="Signed Date" />
                                  <Divider />
                                  <Radio value="DATE_OF_SERVICE" label="Date of Service" />
                                </Stack>
                              </Radio.Group>
                            </Box>
                          </Collapse>
                          <Divider />
                        </Box>

                        <Box>
                          <Checkbox
                            checked={tempDownloadedSelected}
                            onChange={() => setTempDownloadedSelected((prev) => !prev)}
                            label="Not downloaded"
                          />
                        </Box>
                      </>
                    )}
                  </Stack>
                </Stack>
              )}
            </Stack>
          </Box>
        </ScrollArea>

        <Box
          p="md"
          style={{
            borderTop: '1px solid #E0E0E0',
            backgroundColor: 'white'
          }}>
          <Group justify="space-between">
            <Button variant="subtle" onClick={clearAllFilters} disabled={isClearAllDisabled}>
              Clear All
            </Button>
            <Group gap="xs">
              <Button variant="default" onClick={onCloseClick}>
                Cancel
              </Button>
              <Tooltip
                label="Selecting a clinic from a different account will clear your previous clinic selection"
                disabled={!shouldShowTooltip}
                position="top"
                withArrow
                multiline
                w={220}
                styles={{
                  tooltip: {
                    backgroundColor: plum[9],
                    color: 'white'
                  }
                }}>
                <Button
                  onClick={handleApply}
                  disabled={isApplyDisabled}
                  loading={isUpdatingFilter}
                  style={{
                    backgroundColor: plum[8],
                    color: 'white',
                    cursor: shouldShowTooltip ? 'help' : 'pointer'
                  }}>
                  Apply
                </Button>
              </Tooltip>
            </Group>
          </Group>
        </Box>
      </Popover.Dropdown>
    </Popover>
  );
};

export default memo(CombinedFiltersDropdown);
